import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { trpc } from "@blis/trpc/react";

import { Checkbox, CheckboxField } from "@/components/ui/checkbox";
import {
  ComboBox,
  ComboBoxInput,
  ComboBoxItem,
  ComboBoxList,
} from "@/components/ui/combobox";
import {
  Description,
  FieldError,
  Label,
  TextArea,
  TextField,
} from "@/components/ui/field";
import { ItemDetail } from "@/components/ui/list-box";
import {
  NumberField,
  type NumberFieldProps,
  NumberInput,
} from "@/components/ui/number-field";
import type { FormValues } from "./schema";

export function WorkflowActions({ index }: { index: number }) {
  const { watch } = useFormContext<FormValues>();

  const action = watch(`steps.${index}.action`);

  switch (action) {
    case "ADD_RESULT_COMMENT":
      return <AddResultCommentAction index={index} />;
    case "ADD_REFLEX_TEST":
      return <AddReflexTestAction index={index} />;
    default:
      return null;
  }
}

function AddResultCommentAction({ index }: { index: number }) {
  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name={`steps.${index}.payload.comment`}
      render={({ field: { ref, ...field } }) => (
        <TextField {...field}>
          <Label>Comment</Label>
          <TextArea />
          <Description>
            This action will add the above comment to the result, and will be
            visible on the final report
          </Description>
        </TextField>
      )}
    />
  );
}

function AddReflexTestAction({ index }: { index: number }) {
  const { control } = useFormContext();
  const { data: testSpecs } = trpc.admin.testSpec.list.useQuery();

  return (
    <>
      <Controller
        control={control}
        name={`steps.${index}.payload.testSpecId`}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <ComboBox
            menuTrigger="focus"
            {...field}
            selectedKey={field.value ?? null}
            onSelectionChange={field.onChange}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Test spec</Label>
            <ComboBoxInput />
            <ComboBoxList items={testSpecs || []}>
              {(item) => (
                <ComboBoxItem
                  id={item.id}
                  textValue={`${item.name} (${item.code})`}
                >
                  <span className="min-w-0 flex-1 truncate">{item.name}</span>
                  <ItemDetail>{item.code}</ItemDetail>
                </ComboBoxItem>
              )}
            </ComboBoxList>
            <Description>
              This action will add the above test as a reflex test to the parent
              test order.
            </Description>
            <FieldError>{error?.message}</FieldError>
          </ComboBox>
        )}
      />

      {/* <RepeatOptions index={index} /> */}

      <Controller
        control={control}
        name={`steps.${index}.payload.maxCount`}
        render={({ field: { ref, ...field } }) => (
          <NumberFieldWithDefault
            label="Limit"
            className="[&>div]:max-w-40"
            description="The maximum number of reflex tests that can be added."
            {...field}
            defaultValue={1}
            minValue={1}
            isRequired
          />
        )}
      />

      {/* <Controller
        control={control}
        name={`steps.${index}.payload.isDependency`}
        render={({ field: { ref, ...field } }) => (
          <CheckboxField>
            <Checkbox {...field} isSelected={field.value ?? false}>
              Parent result depends on this reflex test
            </Checkbox>
            <Description>
              The parent test's final result will be derived from this reflex
              test result. The reflex test will not appear separately on reports
              and will be automatically removed if the parent test is deleted or
              cleared.
            </Description>
          </CheckboxField>
        )}
      /> */}

      <Controller
        control={control}
        name={`steps.${index}.payload.isBillable`}
        render={({ field: { ref, ...field } }) => (
          <CheckboxField>
            <Checkbox {...field} isSelected={field.value ?? false}>
              Bill for this test
            </Checkbox>
            <Description>
              This test will be billed according to its pricing configuration.
            </Description>
          </CheckboxField>
        )}
      />
    </>
  );
}

function NumberFieldWithDefault({
  name,
  defaultValue,
  label,
  description,
  ...props
}: Omit<NumberFieldProps, "name" | "defaultValue"> & {
  name: string;
  defaultValue: number;
  label?: string;
  description?: string;
}) {
  const { setValue, getValues } = useFormContext();
  useEffect(() => {
    const val = getValues(name);
    if (val === undefined || val === null) {
      setTimeout(() => {
        setValue(name, defaultValue);
      }, 0);
    }
  }, [name, defaultValue, setValue, getValues]);

  return (
    <NumberField name={name} {...props} value={props.value ?? Number.NaN}>
      {label && <Label>{label}</Label>}
      <NumberInput />
      {description && <Description>{description}</Description>}
    </NumberField>
  );
}

function RepeatOptions({ index }: { index: number }) {
  const { control, watch } = useFormContext<FormValues>();
  const repeatType = watch(`steps.${index}.payload.repeatType`);

  return (
    <>
      <Controller
        control={control}
        name={`steps.${index}.payload.repeatType`}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <ComboBox
            menuTrigger="focus"
            {...field}
            selectedKey={field.value ?? "none"}
            onSelectionChange={field.onChange}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <Label>Repeat Type</Label>
            <ComboBoxInput />
            <ComboBoxList
              items={[
                { id: "none", name: "No repeat" },
                { id: "fixed", name: "Fixed number of times" },
                { id: "result", name: "Equivalent to result" },
              ]}
            >
              {(item) => <ComboBoxItem id={item.id}>{item.name}</ComboBoxItem>}
            </ComboBoxList>
            <Description>
              Choose how many times this action should be repeated.
            </Description>
            <FieldError>{error?.message}</FieldError>
          </ComboBox>
        )}
      />

      {repeatType === "fixed" && (
        <Controller
          control={control}
          name={`steps.${index}.payload.repeatCount`}
          render={({ field: { ref, ...field } }) => (
            <NumberFieldWithDefault
              label="Repeat Count"
              className="[&>div]:max-w-40"
              description="The number of times to repeat this action."
              {...field}
              defaultValue={1}
              minValue={1}
              isRequired
            />
          )}
        />
      )}
    </>
  );
}
